import ScrollToTop from "../../components/ScrollToTop.tsx";
import setTitleAndMetaDescription from "../../components/setTitleAndMetaDescription.tsx";
import {imageMap} from "../../images/imageMap.ts";
import QuestionAnswerComponent, {Question} from "../../components/help/QuestionAnswerComponent.tsx";
import useDatalayer from "../../components/useDatalayer.tsx";
import {CallMePhoneNumbers} from "../../util/contact.ts";
import CallMeRTOComponent from "../../components/callMe/CallMeRTOComponent.tsx";

const questions: Question[] = [
    {
        question: 'Hvad er Wifi Mesh?',
        answer: <>
            <p>
                Et mesh netværk er et trådløst netværkssystem designet til at give stabil internetdækning i hele dit hjem eller kontor.
                Det består af flere enheder, også kaldet noder, der arbejder sammen for at forbedre dit wifi-signal.
                Du placerer noderne i områder af din bolig, hvor du ønsker bedre wifi-dækning.
            </p>
            <p>
                Hos os fungerer routeren som hovednoden, og så har du én eller flere satellitnoder, som du placerer i din bolig.
            </p>
        </>,
    },
    {
        question: 'Hvordan fungerer det?',
        answer: <>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Hovednode
            </h4>
            <p>
                Din router er din hovednode og fungerer som en central hub for hele dit mesh netværk.
            </p>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Satellitnoder
            </h4>
            <p>
                Når du køber Wifi Mesh, modtager du en eller flere satellitnoder, som du placerer på områder i din bolig, hvor du ønsker bedre wifi-dækning.
                Noderne forbinder trådløst til hovednoden og hinanden, hvilket skaber et netværk af noder, der alle arbejder sammen om at optimere din wifi-dækning.
            </p>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Wifi uden afbrydelser
            </h4>
            <p>
                Mesh netværket gør det muligt for dine mobiltelefoner, tablets og computere at skifte mellem noderne uden at afbryde forbindelsen, så du med en flydende overgang får en mere stabil internetoplevelse.
            </p>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Selvreparerende netværk
            </h4>
            <p>
                Hvis en node mister forbindelsen, omdirigerer de andre noder automatisk data, så dit netværk forbliver oppe, og du ikke oplever afbrydelser i dit wifi.
            </p>
        </>,
    },
    {
        question: 'Fordele ved Wifi Mesh',
        answer: <>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Forbedret wifi-dækning
            </h4>
            <p>
                Mesh-netværket dækker større områder end en traditionel router, fordi noderne udvider dit wifi-signal.
            </p>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Stabil forbindelse
            </h4>
            <p>
                Du vil opleve en mere stabil forbindelse, da noderne arbejder sammen og reducerer områder i din bolig uden wifi-dækning.
            </p>
            <h4 className="heading heading--montserrat heading--left heading--small">
                Nem opsætning og administration
            </h4>
            <p>
                Wifi Mesh er hurtigt og let at installere. Hvis du alligevel får brug for hjælp, sidder vores erfarne supportere klar til at hjælpe dig.
                Kort sagt, giver et mesh netværk dig bedre dækning og en mere stabil internetforbindelse i hele dit hjem.
            </p>
        </>,
    },
    {
        question: 'Tekniske specifikationer omkring Wifi Mesh',
        answer: <table className="hardware-specifications-table">
            <tbody>
            <tr>
                <th>Model:</th>
                <td>Zyxel WX3100</td>
            </tr>
            <tr>
                <th>Wifi generation:</th>
                <td>Wifi 6</td>
            </tr>
            <tr>
                <th>Antenner:</th>
                <td>2x2 802.11ax-teknologi</td>
            </tr>
            </tbody>
        </table>,
    }
]

export default function WifiMeshPage() {
    ScrollToTop()
    useDatalayer()
    setTitleAndMetaDescription(
        'Få Wifi Mesh til dit fibernet',
        'Hvis du har eller kan få fibernet hos os, tilbyder vi dig også Wifi Mesh. Få en bedre og mere stabil wifi forbindelse med Wifi Mesh.'
    )

    return (
        <div className={"page-products-wifi-mesh"}>
            <div id="hero-container" className="hero-container hero-container--wifi-mesh">
                <div className={'hero-background-wrapper'}>
                    <div className={'hero-background'}/>
                </div>
                <div className="hero-content">
                    <h1 className="heading heading--left heading--white">
                        <span>Bedre wifi <span className="hide-on-small-devices">i hjemmet</span> med</span><strong>Wifi&nbsp;Mesh</strong>
                    </h1>
                    <h2 className="heading heading--left heading--paragraph heading--small heading--white">
                        Til dig der vil have det ultimative wifi.<br/>
                        Perfekt wifi i alle hjørner af hjemmet med Wifi&nbsp;Mesh.<br/>
                        <span>Fra kun 19 kr./md.</span>
                    </h2>
                    <div className="image">
                        <img src={imageMap.products.zyxelExtender} alt="Billede af Hipers nye Wifi Mesh-enhed."/>
                    </div>
                </div>
            </div>

            <CallMeRTOComponent
                phoneNumberSet={CallMePhoneNumbers.WifiMesh}
                rmoQueue={"mesh-rmo"}
                className={"rto--box page-wifi-mesh"}
                differentHeadingOnClosed={false}
                heading={"Lad os hjælpe dig i gang med Wifi Mesh!"}
                headingMobile={"Lad os hjælpe dig i gang!"}
                postCtaText={"Vi er også klar til at besvare dine spørgsmål, hvis du ikke er helt sikker."}
            />

            <section className="section-white section-what">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--big heading--montserrat">Hvad er Wifi Mesh?</h2>
                    </div>
                    <p>
                        Wifi Mesh er løsningen, hvis din wifi-dækning ikke er tilstrækkelig, og du tænker &ldquo;hvordan forstærker jeg mit wifi signal?&rdquo;.
                        Det er et trådløst netværkssystem designet til at forbedre wifi-dækning og signalstyrke i f.eks. hjem og kontorer.
                    </p>
                    <p>
                        Du kan udvide dit wifi-netværk ved at bestille op til tre mesh-enheder, som arbejder sammen for at skabe et bedre trådløst netværk.
                        Mesh-enhederne bliver også kaldt wifi-forstærkere eller -extendere og giver dig bedre wifi i hjemmet.
                        For eksempel kan du se film i stuen, mens dine børn gamer eller ser videoer på deres værelser, selvom de er i hver deres ende af huset.
                    </p>
                </div>
            </section>

            <section className="section-lightgray section-reasons">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--big heading--montserrat">Meget. Mere. Wifi.</h2>
                    </div>
                    <ul>
                        <li>
                            <div className="blurb">
                                <h3 className="heading heading--medium heading--montserrat">
                                    Optimér din wifi-dækning med et Wifi Mesh netværk
                                </h3>
                            </div>
                            <p className={'align-center'}>
                                Med vores Wifi Mesh får du mere stabilt wifi i hjemmet uden ekstra netværks&shy;kabler og forlænger&shy;ledninger.
                                Det eneste du behøver er en stikkontakt til din wifi-extender.
                            </p>
                        </li>
                        <li>
                            <div className="blurb">
                                <h3 className="heading heading--medium heading--montserrat">
                                    Få dækning overalt i huset med en wifi-forstærker
                                </h3>
                            </div>
                            <p className={'align-center'}>
                                Routeren og mesh-enhederne bruger mesh-teknologi.
                                Stol på stabilt internet, hvor som helst i huset, med meget bedre wifi.
                            </p>
                        </li>
                        <li>
                            <div className="blurb">
                                <h3 className="heading heading--medium heading--montserrat">
                                    Altid hurtigt wifi
                                </h3>
                            </div>
                            <p className={'align-center'}>
                                Med Wifi Mesh får du det bedste signal overalt i dit hjem.
                                Det udvider nemlig dit nuværende wifi-signal og giver bedre dækning.
                                En wifi-forstærker i særklasse.
                            </p>
                        </li>
                    </ul>
                </div>
            </section>

            <section className="section-white section-how">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--montserrat heading--big heading--left">
                            Hvordan sætter jeg wifi-extendere op?
                        </h2>
                    </div>
                    <p>
                        Med Wifi Mesh får du én eller flere mesh-extendere, som du skal installere og placere, hvor du ønsker bedre wifi i din bolig.
                        Installationen er let, og du kan gøre det på ingen tid.
                    </p>
                    <p>
                        Følg denne guide:
                    </p>
                    <a
                        className="button button--montserrat"
                        href="/wifi-mesh-vejledning"
                        target="_blank"
                    >
                        Installationsvejledning
                    </a>
                    <p>
                        Når du har parret wifi-extenderne med din router, opretter de automatisk trådløs forbindelse til routeren.
                        Du har nu stærkt signal og bedre wifi i hele huset.
                        Simplere bliver det ikke.
                    </p>
                </div>
            </section>

            <section className="section-white section-tips">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--montserrat heading--big heading--left">
                            Perfekt wifi med wifi-forstærker
                        </h2>
                    </div>
                    <p>
                        Sig farvel til ustabile videomøder og sure miner fra børnene, når wifi driller.
                        Med Wifi Mesh bestemmer du selv, om du vil have en, to eller tre mesh-enheder.
                        Vi har din ryg, uanset om du vil have perfekt wifi i alle kroge eller f.eks. bare forbedre det i soveværelset.
                    </p>
                </div>
            </section>

            <section className="section-white section-faq">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--montserrat heading--big">
                            Mest stillede spørgsmål om Wifi Mesh
                        </h2>
                    </div>
                    <QuestionAnswerComponent questions={questions}/>
                </div>
            </section>
        </div>
    )
}
